// Copyright 2015-2023 Nstream, inc.
// All rights reserved.

import type {Value} from "@swim/structure";
import {Recon} from "@swim/recon";
import {DateTimeFormat} from "@swim/time";
import {ValueDownlink} from "@swim/client";
import type {Trait} from "@swim/model";
import {ViewRef} from "@swim/view";
import {ViewSet} from "@swim/view";
import {TraitViewRef} from "@swim/controller";
import type {PanelView} from "@swim/panel";
import type {ColLayout} from "@swim/table";
import type {ColView} from "@swim/table";
import type {HeaderView} from "@swim/table";
import type {TextCellView} from "@swim/table";
import type {TextColView} from "@swim/table";
import {TimeTableController} from "@nstream/widget";
import {AppLogMessageController} from "./AppLogMessageController";

/** @public */
export class AppLogController extends TimeTableController {
  @TraitViewRef({
    extends: true,
    initView(panelView: PanelView): void {
      super.initView(panelView);
      panelView.set({
        headerTitle: "Log",
        headerSubtitle: "App",
      });
      this.owner.tablePanel.insertView().style.set({
        marginTop: 48,
      });
      this.owner.table.insertView();
      this.owner.header.insertView();
    },
  })
  override readonly panel!: TraitViewRef<this, Trait, PanelView> & TimeTableController["panel"];

  @ViewRef({
    extends: true,
    createView(): HeaderView {
      const headerView = super.createView() as HeaderView;
      (this.owner.nameCol.insertView(headerView) as TextColView).set({
        label: "Time",
      });
      (this.owner.latestCol.insertView(headerView) as TextColView).set({
        label: "Message",
      });
      return headerView;
    },
  })
  override readonly header!: ViewRef<this, HeaderView> & TimeTableController["header"];

  @ViewSet({
    extends: true,
    getLayout(colView: ColView): ColLayout | null {
      let colLayout = super.getLayout(colView) as ColLayout | null;
      if (colLayout !== null) {
        colLayout = colLayout.withFlex(8, 0, 0);
      }
      return colLayout;
    },
  })
  override readonly cols!: ViewSet<this, ColView> & TimeTableController["cols"];

  protected createAppLogMessageController(time: number, type: string, value: Value): AppLogMessageController {
    const appLogMessageController = new AppLogMessageController();
    const dateFormat = DateTimeFormat.pattern("%i:%M:%S %p %b %e");
    (appLogMessageController.nameCell.insertView() as TextCellView).set({
      content: dateFormat.format(time),
    });
    const valueCell = appLogMessageController.latestCell.insertView() as TextCellView;
    value.forEach((item) => {
      if (!item.get("time").isDefined()) {
        valueCell.content.set(type + Recon.toString(item));
        appLogMessageController.latestCell.insertView();
      }
    });
    return appLogMessageController;
  }

  protected updateAppLogMessageController(appLogMessageController: AppLogMessageController, value: Value): AppLogMessageController {
    (appLogMessageController.latestCell.insertView() as TextCellView).set({
      content: Recon.toString(value),
    });
    return appLogMessageController;
  }

  @ValueDownlink({
    laneUri: "infoLog",
    consumed: true,
    didSet(value: Value): void {
      //console.log("AppLogController.infoLogDownlink.didSet:", value.get("info"));
      const key = value.get("info").get("time");
      const time = key.numberValue(0);
      let appLogMessageController = this.owner.getChild(Recon.toString(key), AppLogMessageController);
      if (appLogMessageController === null) {
        appLogMessageController = this.owner.createAppLogMessageController(time, "Info: ", value);
        this.owner.series.addController(appLogMessageController, void 0, Recon.toString(value));
      } else {
        //TODO- update
      }
    }
  })
  readonly infoLogDownlink!: ValueDownlink<this, Value>;

  @ValueDownlink({
    laneUri: "debugLog",
    consumed: true,
    didSet(value: Value): void {
      //console.log("AppLogController.debugLogDownlink.didSet:", value.get("debug"));
      const key = value.get("debug").get("time");
      const time = key.numberValue(0);
      let appLogMessageController = this.owner.getChild(Recon.toString(key), AppLogMessageController);
      if (appLogMessageController === null) {
        appLogMessageController = this.owner.createAppLogMessageController(time, "Debug: ", value);
        this.owner.series.addController(appLogMessageController, void 0, Recon.toString(value));
      } else {
        //TODO- update
      }
    }
  })
  readonly debugLogDownlink!: ValueDownlink<this, Value>;
}
